import { useState } from "react";
import {
  Navigation,
  Clock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Star,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Collapsible, CollapsibleContent } from "@/components/ui/Collapsible";
import { Card, CardContent } from "@/components/ui/Card";
import type { SpecialistsDetails } from "@/api/contracts/specialists.contract";
import {
  buildSearchAvailability,
  getSpecialistPrice,
} from "@/utils/specialist";
import {
  formatSlotTime,
  getSlotDateDisplay,
  type SlotLike,
} from "@/utils/appointments";
import { hasValidWebsite, websiteHref } from "@/utils/url";
import { STORAGE_KEYS } from "@/constants/storage";
import { SlotRow } from "./SlotRow";
import {
  CARD_STYLE,
  TELEHEALTH_BADGE,
  IN_PERSON_BADGE,
  PREFERRED_BADGE,
} from "../constants";

type Slot = SlotLike;

// Everything identifying the appointment comes from the clicked slot, so the
// booking form submits the same slot the API validates against.
function buildBookingSearch(s: SpecialistsDetails, slot: Slot) {
  const practice = s.practices.find(
    (item) => item.practiceLocationId === slot.locationId,
  );

  return {
    specialistId: s.specialistId,
    timeSlotId: slot.id,
    locationId: slot.locationId ?? undefined,
    locationAddress: practice?.practiceAddress ?? undefined,
    appointmentDate: new Date(slot.dateTime).toISOString(),
    date: getSlotDateDisplay(slot),
    time: formatSlotTime(slot),
    doctor:
      `${s.specialistTitle ?? ""} ${s.specialistFirstName ?? ""} ${s.specialistLastName ?? ""} ${s.specialistSuffix ?? ""}`
        .replace(/\s+/g, " ")
        .trim(),
  };
}

export function SpecialistCard({
  specialist,
  onLocationHover,
}: {
  specialist: SpecialistsDetails;
  onLocationHover?: (mapKey: string | null) => void;
}) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const primaryPractice = specialist.practices[0];
  const [specialistSlug, practiceSlug] = primaryPractice?.slug.split("/") ?? [];
  const canViewProfile = !!specialistSlug && !!practiceSlug;
  const availability = buildSearchAvailability(specialist);
  const hasInPerson = availability.some((g) => !g.isTelehealth);
  const hasTelehealth = availability.some((g) => g.isTelehealth);

  const onBook = (slot: Slot) => {
    sessionStorage.setItem(STORAGE_KEYS.BACK_FROM_BOOKING, "Search Results");
    navigate({ to: "/booking", search: buildBookingSearch(specialist, slot) });
  };

  const onProfile = () => {
    if (!canViewProfile) return;
    sessionStorage.setItem(
      STORAGE_KEYS.BACK_FROM_SPECIALIST_PROFILE,
      "Search Results",
    );
    navigate({
      to: "/specialist-profile/$specialistSlug/$practiceSlug",
      params: { specialistSlug, practiceSlug },
    });
  };

  return (
    <Card
      className={`${CARD_STYLE} hover:shadow-[0_6px_16px_0_rgba(46,101,255,0.15)] transition-shadow duration-200`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-4">
          <div className="flex flex-row gap-3 md:gap-4 flex-1">
            <div className="shrink-0">
              {specialist.specialistProfilePicture ? (
                <img
                  src={specialist.specialistProfilePicture}
                  alt={`${specialist.specialistTitle ?? ""} ${specialist.specialistFirstName ?? ""} ${specialist.specialistLastName ?? ""} ${specialist.specialistSuffix ?? ""}`}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove("hidden");
                  }}
                />
              ) : null}
              <div
                className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-muted flex items-center justify-center ${specialist.specialistProfilePicture ? "hidden" : ""}`}
              >
                <span className="text-primary font-semibold text-base sm:text-lg">
                  {`${specialist.specialistFirstName?.[0] ?? ""}${specialist.specialistLastName?.[0] ?? ""}`.toUpperCase() ||
                    "?"}
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-1.5 mb-1.5 items-start">
                {canViewProfile ? (
                  <button
                    type="button"
                    onClick={onProfile}
                    className="headline text-lg sm:text-xl text-primary hover:text-primary/80 transition-colors truncate max-w-full"
                  >
                    {specialist.specialistTitle}{" "}
                    {specialist.specialistFirstName}{" "}
                    {specialist.specialistLastName}{" "}
                    {specialist.specialistSuffix}
                  </button>
                ) : (
                  <span className="headline text-lg sm:text-xl text-primary truncate max-w-full">
                    {specialist.specialistTitle}{" "}
                    {specialist.specialistFirstName}{" "}
                    {specialist.specialistLastName}{" "}
                    {specialist.specialistSuffix}
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  {specialist.isRecommended && (
                    <span
                      title="Preferred doctor — highly recommended"
                      aria-label="Preferred doctor"
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${PREFERRED_BADGE.className}`}
                    >
                      <Star
                        className="h-3 w-3 shrink-0 fill-amber-500 text-amber-500"
                        aria-hidden="true"
                      />
                      {PREFERRED_BADGE.label}
                    </span>
                  )}
                  {hasInPerson && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${IN_PERSON_BADGE.className}`}
                    >
                      {IN_PERSON_BADGE.label}
                    </span>
                  )}
                  {hasTelehealth && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${TELEHEALTH_BADGE.className}`}
                    >
                      {TELEHEALTH_BADGE.label}
                    </span>
                  )}
                </div>
              </div>
              <p className="body-copy text-base text-foreground mb-1.5 sm:mb-2">
                {primaryPractice?.specialties}
              </p>
              {primaryPractice &&
                hasValidWebsite(primaryPractice.practiceWebSiteUrl) && (
                  <a
                    href={websiteHref(primaryPractice.practiceWebSiteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Website
                  </a>
                )}
            </div>
          </div>
          <div className="shrink-0 text-left md:text-right mt-2 md:mt-0 flex flex-row md:flex-col items-start md:items-end justify-between">
            <div>
              <p className="headline text-lg sm:text-xl font-bold text-foreground mb-0.5">
                ${getSpecialistPrice(specialist)}
              </p>
              <p className="body-copy text-sm text-muted-foreground">
                Consultation fee
              </p>
            </div>
            {hasInPerson &&
              primaryPractice &&
              primaryPractice.practiceDistance > 0 &&
              (primaryPractice.directionUrl ? (
                <a
                  href={primaryPractice.directionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-semibold text-base text-foreground whitespace-nowrap hover:text-secondary transition-colors"
                >
                  <Navigation className="h-4 w-4 shrink-0 text-secondary" />
                  {primaryPractice.practiceDistance.toFixed(1)} mi
                </a>
              ) : (
                <span className="inline-flex items-center gap-1.5 font-semibold text-base text-foreground whitespace-nowrap">
                  <Navigation className="h-4 w-4 shrink-0 text-secondary" />
                  {primaryPractice.practiceDistance.toFixed(1)} mi
                </span>
              ))}
          </div>
        </div>

        <hr className="border-t border-border mb-4" />

        <div>
          <div className="flex items-center gap-1 mb-3">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="body-copy text-base font-medium text-foreground">
              Available Times
            </span>
          </div>

          {!availability.length ? (
            <p className="text-sm text-muted-foreground">
              Contact for availability
            </p>
          ) : (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              {!isExpanded && (
                <div className="space-y-3">
                  {availability.slice(0, 2).map((g) => (
                    <SlotRow
                      key={g.key}
                      date={g.date}
                      slots={g.slots}
                      location={g.locationLabel}
                      isTelehealth={g.isTelehealth}
                      limit={expandedDates.has(g.key) ? undefined : 3}
                      onBook={onBook}
                      onLocationEnter={() => onLocationHover?.(g.mapKey)}
                      onLocationLeave={() => onLocationHover?.(null)}
                      onShowMore={
                        g.slots.length > 3
                          ? () =>
                              setExpandedDates((s) => new Set(s).add(g.key))
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
              <CollapsibleContent className="space-y-3 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
                {availability.map((g) => (
                  <SlotRow
                    key={g.key}
                    date={g.date}
                    slots={g.slots}
                    location={g.locationLabel}
                    isTelehealth={g.isTelehealth}
                    onBook={onBook}
                    onLocationEnter={() => onLocationHover?.(g.mapKey)}
                    onLocationLeave={() => onLocationHover?.(null)}
                  />
                ))}
                {canViewProfile && (
                  <div className="pt-3 border-t border-border">
                    <button
                      type="button"
                      onClick={onProfile}
                      className="text-primary hover:text-primary/80 text-sm"
                    >
                      View Full Calendar on Profile →
                    </button>
                  </div>
                )}
              </CollapsibleContent>
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full mt-3 flex items-center justify-center gap-1 text-primary hover:text-primary/80 hover:bg-primary/5 py-2 rounded-md transition-colors text-sm"
              >
                {isExpanded ? (
                  <>
                    Show less <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Show all availability <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            </Collapsible>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
